import { BackgroundLayer, px, pct, toCss, zero } from './background';
import {
  Accent,
  Background,
  BackgroundMode,
  Font,
  Preset,
  Primary,
} from './types';

/**
 * The backgrounds shown in the picker, in picker order. Each is a stack of
 * layers rather than a CSS string, so a surface that cannot parse CSS still
 * paints the real thing; `css` is the web's rendering of that stack.
 */
const background = (
  id: string,
  name: string,
  mode: BackgroundMode,
  layers: BackgroundLayer[],
): Background => ({ id, name, mode, layers, css: toCss(layers) });

export const BACKGROUNDS: Background[] = [
  background('ink', 'Ink Black', 'Dark', [{ type: 'solid', color: '#0E0E0E' }]),
  background('white', 'Paper White', 'Light', [
    { type: 'solid', color: '#FFFFFF' },
  ]),
  background('charcoal', 'Warm Charcoal', 'Dark', [
    { type: 'solid', color: '#141210' },
  ]),
  background('bone', 'Bone', 'Light', [{ type: 'solid', color: '#F5F1E8' }]),
  background('slate', 'Slate', 'Dark', [{ type: 'solid', color: '#1C1F24' }]),
  background('smoke', 'Smoke', 'Light', [
    {
      type: 'radial',
      shape: 'circle',
      center: [pct(30), pct(25)],
      stops: [
        { color: 'rgba(90,90,95,0.16)' },
        { color: 'transparent', at: [pct(50)] },
      ],
    },
    {
      type: 'radial',
      shape: 'circle',
      center: [pct(72), pct(70)],
      stops: [
        { color: 'rgba(70,70,80,0.13)' },
        { color: 'transparent', at: [pct(48)] },
      ],
    },
    { type: 'solid', color: '#F1F1EF' },
  ]),
  background('grain', 'Blackwork Grain', 'Dark', [
    {
      type: 'radial',
      stops: [
        { color: 'rgba(255,255,255,0.055)', at: [px(0.5)] },
        { color: 'transparent', at: [px(0.6)] },
      ],
      tile: { position: [zero, zero], size: [px(3), px(3)] },
    },
    {
      type: 'radial',
      stops: [
        { color: 'rgba(255,255,255,0.04)', at: [px(0.5)] },
        { color: 'transparent', at: [px(0.6)] },
      ],
      tile: { position: [px(1.5), px(1.5)], size: [px(4), px(4)] },
    },
    { type: 'solid', color: '#0A0A0A' },
  ]),
  background('parchment', 'Parchment', 'Light', [
    {
      type: 'radial',
      shape: 'circle',
      center: [pct(20), pct(15)],
      stops: [
        { color: 'rgba(160,130,80,0.14)' },
        { color: 'transparent', at: [pct(45)] },
      ],
    },
    {
      type: 'radial',
      shape: 'circle',
      center: [pct(78), pct(65)],
      stops: [
        { color: 'rgba(140,110,60,0.12)' },
        { color: 'transparent', at: [pct(42)] },
      ],
    },
    {
      type: 'linear',
      angle: 100,
      repeating: true,
      stops: [
        { color: 'rgba(120,95,50,0.05)', at: [zero, px(2)] },
        { color: 'transparent', at: [px(2), px(7)] },
      ],
    },
    { type: 'solid', color: '#EFE6D2' },
  ]),
  background('midnight', 'Midnight Fade', 'Dark', [
    {
      type: 'linear',
      angle: 180,
      stops: [
        { color: '#0A1A3D', at: [pct(0)] },
        { color: '#050B1C', at: [pct(62)] },
        { color: '#000000', at: [pct(100)] },
      ],
    },
  ]),
  background('traditional', 'Traditional', 'Dark', [
    {
      type: 'linear',
      angle: 45,
      repeating: true,
      stops: [
        { color: 'rgba(255,255,255,0.05)', at: [zero, px(1)] },
        { color: 'transparent', at: [px(1), px(14)] },
      ],
    },
    {
      type: 'linear',
      angle: -45,
      repeating: true,
      stops: [
        { color: 'rgba(255,255,255,0.04)', at: [zero, px(1)] },
        { color: 'transparent', at: [px(1), px(18)] },
      ],
    },
    {
      type: 'radial',
      shape: 'circle',
      center: [pct(50), pct(30)],
      stops: [
        { color: 'rgba(255,255,255,0.05)', at: [zero, px(12)] },
        { color: 'transparent', at: [px(13)] },
      ],
    },
    { type: 'solid', color: '#0C1B33' },
  ]),
  background('oxblood', 'Oxblood', 'Dark', [
    {
      type: 'linear',
      angle: 180,
      stops: [
        { color: '#1A0A0C', at: [pct(0)] },
        { color: '#2A0C11', at: [pct(55)] },
        { color: '#3B0F14', at: [pct(100)] },
      ],
    },
  ]),
  background('sage', 'Sage', 'Light', [
    {
      type: 'radial',
      shape: 'ellipse',
      radii: [px(60), px(22)],
      center: [pct(18), pct(20)],
      stops: [
        { color: 'rgba(46,59,47,0.07)', at: [zero, pct(99)] },
        { color: 'transparent' },
      ],
    },
    {
      type: 'radial',
      shape: 'ellipse',
      radii: [px(22), px(60)],
      center: [pct(74), pct(58)],
      stops: [
        { color: 'rgba(46,59,47,0.06)', at: [zero, pct(99)] },
        { color: 'transparent' },
      ],
    },
    {
      type: 'radial',
      shape: 'ellipse',
      radii: [px(48), px(18)],
      center: [pct(52), pct(86)],
      stops: [
        { color: 'rgba(46,59,47,0.055)', at: [zero, pct(99)] },
        { color: 'transparent' },
      ],
    },
    {
      type: 'linear',
      angle: 62,
      repeating: true,
      stops: [
        { color: 'rgba(46,59,47,0.045)', at: [zero, px(1)] },
        { color: 'transparent', at: [px(1), px(58)] },
      ],
    },
    { type: 'solid', color: '#DDE3D5' },
  ]),
  background('seigaiha', 'Seigaiha', 'Dark', [
    {
      type: 'radial',
      shape: 'circle',
      center: [pct(50), pct(100)],
      stops: [
        { color: 'transparent', at: [px(8)] },
        { color: 'rgba(255,255,255,0.16)', at: [px(9), px(10)] },
        { color: 'transparent', at: [px(11), px(16)] },
        { color: 'rgba(255,255,255,0.13)', at: [px(17), px(18)] },
        { color: 'transparent', at: [px(19), px(24)] },
        { color: 'rgba(255,255,255,0.1)', at: [px(25), px(26)] },
        { color: 'transparent', at: [px(27)] },
      ],
      tile: { position: [zero, zero], size: [px(52), px(26)] },
    },
    {
      type: 'radial',
      shape: 'circle',
      center: [pct(50), pct(100)],
      stops: [
        { color: 'transparent', at: [px(8)] },
        { color: 'rgba(255,255,255,0.16)', at: [px(9), px(10)] },
        { color: 'transparent', at: [px(11), px(16)] },
        { color: 'rgba(255,255,255,0.13)', at: [px(17), px(18)] },
        { color: 'transparent', at: [px(19), px(24)] },
        { color: 'rgba(255,255,255,0.1)', at: [px(25), px(26)] },
        { color: 'transparent', at: [px(27)] },
      ],
      tile: { position: [px(26), px(13)], size: [px(52), px(26)] },
    },
    { type: 'solid', color: '#142A5C' },
  ]),
  background('flashsheet', 'Flash Sheet', 'Light', [
    {
      type: 'radial',
      shape: 'circle',
      center: [pct(22), pct(18)],
      stops: [
        { color: 'rgba(120,60,20,0.16)', at: [zero, px(34)] },
        { color: 'transparent', at: [px(35)] },
      ],
    },
    {
      type: 'radial',
      shape: 'circle',
      center: [pct(78), pct(62)],
      stops: [
        { color: 'rgba(120,60,20,0.14)', at: [zero, px(26)] },
        { color: 'transparent', at: [px(27)] },
      ],
    },
    {
      type: 'linear',
      angle: 0,
      repeating: true,
      stops: [
        { color: 'rgba(120,80,40,0.09)', at: [zero, px(1)] },
        { color: 'transparent', at: [px(1), px(44)] },
      ],
    },
    {
      type: 'linear',
      angle: 90,
      repeating: true,
      stops: [
        { color: 'rgba(120,80,40,0.09)', at: [zero, px(1)] },
        { color: 'transparent', at: [px(1), px(44)] },
      ],
    },
    { type: 'solid', color: '#EFE3C8' },
  ]),
  background('violet', 'Electric Violet', 'Dark', [
    { type: 'solid', color: '#5B21B6' },
  ]),
  background('hotpink', 'Hot Pink', 'Dark', [
    { type: 'solid', color: '#D01B63' },
  ]),
  background('teal', 'Deep Teal', 'Dark', [
    { type: 'solid', color: '#0B5F63' },
  ]),
  background('klein', 'Klein Blue', 'Dark', [
    { type: 'solid', color: '#1B27C9' },
  ]),
  background('sunset', 'Sunset', 'Dark', [
    {
      type: 'linear',
      angle: 170,
      stops: [
        { color: '#C2410C', at: [pct(0)] },
        { color: '#BE185D', at: [pct(55)] },
        { color: '#6B21A8', at: [pct(100)] },
      ],
    },
  ]),
  background('vapor', 'Vaporwave', 'Dark', [
    {
      type: 'linear',
      angle: 170,
      stops: [
        { color: '#6D28D9', at: [pct(0)] },
        { color: '#4338CA', at: [pct(50)] },
        { color: '#0E7490', at: [pct(100)] },
      ],
    },
  ]),
  background('bloodorange', 'Blood Orange', 'Dark', [
    {
      type: 'linear',
      angle: 180,
      stops: [
        { color: '#B91C1C', at: [pct(0)] },
        { color: '#7F1D1D', at: [pct(55)] },
        { color: '#45090C', at: [pct(100)] },
      ],
    },
  ]),
  background('crimson', 'Crimson Fade', 'Dark', [
    {
      type: 'linear',
      angle: 180,
      stops: [
        { color: '#3D0A0A', at: [pct(0)] },
        { color: '#150303', at: [pct(62)] },
        { color: '#000000', at: [pct(100)] },
      ],
    },
  ]),
];

/**
 * Reachable only by selecting a preset. When one is stored, the picker prepends
 * it labelled "From preset" so the selection stays visible and re-selectable.
 */
export const PRESET_ONLY_BACKGROUNDS: Background[] = [
  background('chrome', 'Chrome', 'Dark', [
    {
      type: 'linear',
      angle: 0,
      repeating: true,
      stops: [
        { color: 'rgba(255,255,255,0.045)', at: [zero, px(1)] },
        { color: 'transparent', at: [px(1), px(4)] },
      ],
    },
    {
      type: 'linear',
      angle: 118,
      stops: [
        { color: 'rgba(255,255,255,0.14)', at: [pct(0)] },
        { color: 'rgba(255,255,255,0.02)', at: [pct(22)] },
        { color: 'rgba(255,255,255,0.11)', at: [pct(44)] },
        { color: 'rgba(255,255,255,0.01)', at: [pct(62)] },
        { color: 'rgba(255,255,255,0.09)', at: [pct(82)] },
        { color: 'rgba(255,255,255,0.02)', at: [pct(100)] },
      ],
    },
    { type: 'solid', color: '#0A0A0B' },
  ]),
];

export const PRIMARIES: Primary[] = [
  { id: 'ink', name: 'Ink', hex: '#111111', on: '#FFFFFF' },
  { id: 'white', name: 'White', hex: '#FFFFFF', on: '#111111' },
  // Signal Red is a button fill, not a heading color — the unthemed site heads in ink.
  {
    id: 'porter',
    name: 'Signal Red',
    hex: '#FF3008',
    on: '#FFFFFF',
    heading: '#333333',
  },
  { id: 'antiquegold', name: 'Antique Gold', hex: '#C9A227', on: '#141210' },
  { id: 'bone', name: 'Bone', hex: '#EDE6DB', on: '#1A0A0C' },
  { id: 'forestink', name: 'Forest Ink', hex: '#2E3B2F', on: '#F2F5EF' },
  { id: 'chrome', name: 'Chrome', hex: '#E8E8EC', on: '#0A0A0B' },
  { id: 'electric', name: 'Electric Blue', hex: '#2563EB', on: '#FFFFFF' },
  { id: 'indigo', name: 'Indigo', hex: '#4F46E5', on: '#FFFFFF' },
  { id: 'violet', name: 'Violet', hex: '#7C3AED', on: '#FFFFFF' },
  { id: 'fuchsia', name: 'Fuchsia', hex: '#D946EF', on: '#FFFFFF' },
  { id: 'hotpink', name: 'Hot Pink', hex: '#EC4899', on: '#FFFFFF' },
  { id: 'coral', name: 'Coral', hex: '#FB7185', on: '#2A0713' },
  { id: 'vermillion', name: 'Vermillion', hex: '#EF4444', on: '#FFFFFF' },
  { id: 'tangerine', name: 'Tangerine', hex: '#FF6B1A', on: '#FFFFFF' },
  { id: 'amber', name: 'Amber', hex: '#F59E0B', on: '#231400' },
  { id: 'acid', name: 'Acid Yellow', hex: '#FACC15', on: '#1A1400' },
  { id: 'lime', name: 'Lime', hex: '#A3E635', on: '#14210A' },
  { id: 'emerald', name: 'Emerald', hex: '#059669', on: '#FFFFFF' },
  { id: 'teal', name: 'Teal', hex: '#14B8A6', on: '#052B31' },
  { id: 'cyan', name: 'Cyan', hex: '#06B6D4', on: '#052B31' },
];

export const ACCENTS: Accent[] = [
  { id: 'porter', name: 'Signal Red', hex: '#FF3008' },
  { id: 'antiquegold', name: 'Antique Gold', hex: '#C9A227' },
  { id: 'bone', name: 'Bone', hex: '#EDE6DB' },
  { id: 'terracotta', name: 'Terracotta', hex: '#A85536' },
  { id: 'acidgreen', name: 'Acid Green', hex: '#B6FF3C' },
  { id: 'sky', name: 'Sky', hex: '#38BDF8' },
  { id: 'cyan', name: 'Cyan', hex: '#22D3EE' },
  { id: 'mint', name: 'Mint', hex: '#34D399' },
  { id: 'limebright', name: 'Lime', hex: '#BEF264' },
  { id: 'yellow', name: 'Yellow', hex: '#FDE047' },
  { id: 'amber', name: 'Amber', hex: '#FBBF24' },
  { id: 'orange', name: 'Orange', hex: '#FB923C' },
  { id: 'red', name: 'Red', hex: '#F87171' },
  { id: 'pink', name: 'Pink', hex: '#F472B6' },
  { id: 'fuchsia', name: 'Fuchsia', hex: '#E879F9' },
  { id: 'violet', name: 'Violet', hex: '#A78BFA' },
  { id: 'indigo', name: 'Indigo', hex: '#818CF8' },
  { id: 'white', name: 'White', hex: '#FFFFFF' },
  { id: 'silver', name: 'Silver', hex: '#C0C0C0' },
  { id: 'gold', name: 'Gold', hex: '#D4A017' },
  { id: 'black', name: 'Black', hex: '#000000' },
];

/** Usable for headings and body. */
export const CORE_FONTS: Font[] = [
  {
    id: 'inter',
    name: 'Inter',
    stack: "'Inter', sans-serif",
    query: 'Inter:wght@400;500;600;700',
    track: '-0.01em',
  },
  {
    id: 'roboto',
    name: 'Roboto',
    stack: "'Roboto', sans-serif",
    query: 'Roboto:wght@400;500;700',
    track: '0em',
  },
  {
    id: 'opensans',
    name: 'Open Sans',
    stack: "'Open Sans', sans-serif",
    query: 'Open+Sans:wght@400;600;700',
    track: '0em',
  },
  {
    id: 'lato',
    name: 'Lato',
    stack: "'Lato', sans-serif",
    query: 'Lato:wght@400;700',
    track: '0em',
  },
  {
    id: 'montserrat',
    name: 'Montserrat',
    stack: "'Montserrat', sans-serif",
    query: 'Montserrat:wght@400;500;600;700;800',
    track: '0em',
  },
  {
    id: 'merriweather',
    name: 'Merriweather',
    stack: "'Merriweather', serif",
    query: 'Merriweather:wght@400;700',
    track: '0em',
  },
  {
    id: 'playfair',
    name: 'Playfair Display',
    stack: "'Playfair Display', serif",
    query: 'Playfair+Display:wght@400;500;700',
    track: '0em',
  },
];

/** Headings only — body falls back to Inter. */
export const DISPLAY_FONTS: Font[] = [
  {
    id: 'bebas',
    name: 'Bebas Neue',
    stack: "'Bebas Neue', sans-serif",
    query: 'Bebas+Neue',
    track: '0.02em',
  },
  {
    id: 'oswald',
    name: 'Oswald',
    stack: "'Oswald', sans-serif",
    query: 'Oswald:wght@400;500;700',
    track: '0em',
  },
  {
    id: 'cinzel',
    name: 'Cinzel',
    stack: "'Cinzel', serif",
    query: 'Cinzel:wght@400;700',
    track: '0em',
  },
  {
    id: 'pirata',
    name: 'Pirata One',
    stack: "'Pirata One', serif",
    query: 'Pirata+One',
    track: '0em',
  },
  {
    id: 'elite',
    name: 'Special Elite',
    stack: "'Special Elite', serif",
    query: 'Special+Elite',
    track: '0em',
  },
  {
    id: 'cormorant',
    name: 'Cormorant Garamond',
    stack: "'Cormorant Garamond', serif",
    query: 'Cormorant+Garamond:wght@400;600;700',
    track: '0em',
  },
  {
    id: 'bodoni',
    name: 'Bodoni Moda',
    stack: "'Bodoni Moda', serif",
    query: 'Bodoni+Moda:wght@400;600;700',
    track: '0em',
  },
  {
    id: 'ebgaramond',
    name: 'EB Garamond',
    stack: "'EB Garamond', serif",
    query: 'EB+Garamond:wght@400;500;600',
    track: '0em',
  },
  {
    id: 'grotesk',
    name: 'Space Grotesk',
    stack: "'Space Grotesk', sans-serif",
    query: 'Space+Grotesk:wght@400;500;700',
    track: '0em',
  },
];

export const PRESETS: Preset[] = [
  {
    id: 'ink',
    name: 'Ink',
    bg: 'ink',
    primary: 'white',
    accent: 'gold',
    font: 'bebas',
  },
  {
    id: 'modern',
    name: 'Modern',
    bg: 'white',
    primary: 'porter',
    accent: 'porter',
    font: 'montserrat',
  },
  {
    id: 'parlor',
    name: 'Parlor',
    bg: 'parchment',
    primary: 'ink',
    accent: 'gold',
    font: 'playfair',
  },
  {
    id: 'violet',
    name: 'Violet',
    bg: 'violet',
    primary: 'coral',
    accent: 'yellow',
    font: 'oswald',
  },
  {
    id: 'gilded',
    name: 'Gilded',
    bg: 'charcoal',
    primary: 'antiquegold',
    accent: 'antiquegold',
    font: 'cormorant',
  },
  {
    id: 'oxblood',
    name: 'Oxblood',
    bg: 'oxblood',
    primary: 'bone',
    accent: 'bone',
    font: 'bodoni',
  },
  {
    id: 'botanic',
    name: 'Botanic',
    bg: 'sage',
    primary: 'forestink',
    accent: 'terracotta',
    font: 'ebgaramond',
  },
  {
    id: 'chrome',
    name: 'Chrome',
    bg: 'chrome',
    primary: 'chrome',
    accent: 'acidgreen',
    font: 'grotesk',
  },
];

/**
 * The fallback for any slot a shop has not chosen. Reproduces Porter's existing
 * booking site, so picking one option changes that one thing rather than
 * repainting the page.
 */
export const DEFAULT_THEME_IDS = {
  backgroundId: 'white',
  primaryId: 'porter',
  accentId: 'porter',
  fontId: 'montserrat',
  presetId: null as string | null,
};

export const BODY_FALLBACK_FONT = "'Inter', sans-serif";

/** The same fallback as a family name, for renderers that take no stack. */
export const BODY_FALLBACK_FAMILY = CORE_FONTS[0].name;
